import React, { useState, useMemo } from "react";
import DataTable from "react-data-table-component";
import { Modal, ModalBody, ModalFooter, Button, Spinner, Alert } from "reactstrap";
import {
  Card,
  CardBody,
  CardTitle,
  Badge,
  Row,
  Col,
} from "reactstrap";
import SharedUsersModal from "./SharedUsersModal";
import * as url from "../../../../helpers/url_helper";
import ConfirmationModal from "../../../../components/Common/ConfirmationModal";
import request from "../../../../helpers/request";

interface InterfacesharedList {
  sharedList: any[];
  parentList: any[];
  grantorUserId: string;
  premiseId: string;
  onShareSuccess?: () => void;
}

function SharedUsersList({
  sharedList,
  parentList,
  grantorUserId,
  premiseId,
  onShareSuccess,
}: InterfacesharedList) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [listMode, setListMode] = useState<"shared" | "parent" | null>(null);

  const [shareModal, setShareModal] = useState<{
    open: boolean;
    mode: "create" | "edit";
    data?: any;
  }>({ open: false, mode: "create" });

  //  confirmation-modal 
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const listType: any[] = useMemo(() => {
    if (!listMode) return [];
    if (listMode === "shared") return sharedList;
    if (listMode === "parent") return parentList;
    return [];
  }, [sharedList, parentList, listMode]);

  const cancleBtnClick = () => {
    setIsModalOpen(false);
    setListMode(null);
  };
  const toggleRequestModel = () => {
    setIsModalOpen(!isModalOpen);
    setListMode(null);
  };

  const openEditModal = (data: any) => {
    setShareModal({ open: true, mode: "edit", data });
  };

  const openDeleteModal = (data: any) => {
    setDeleteError(null);
    setDeleteTarget(data);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {

    const accessId =  deleteTarget?._id 
    if (!accessId) {
      console.warn("Missing accessId on card data — cannot delete.");
      setDeleteModalOpen(false);
      setDeleteTarget(null);
      return;
    }

    setDeletingId(accessId);
    setDeleteError(null);
    try {
      const deleteUrl = `${url.BASE_URL}${url.DELETE_SHARE_ACCESS_USER}`;
      await request.post(deleteUrl, {
        accessId,
      });

      onShareSuccess?.();
      setDeleteModalOpen(false);
      setDeleteTarget(null);
    } catch (err: any) {
      setDeleteError(
        err?.response?.data?.msg ||
          err?.response?.data?.message ||
          err?.message ||
          "Something went wrong while removing access."
      );
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div>
      <div className="d-flex gap-3">
        <button
          className="btn btn-dark btn-sm"
          onClick={() => {
            setIsModalOpen(true);
            setListMode("shared");
          }}
          // disabled={sharedList?.length === 0}
        >
          Shared User ({sharedList?.length || 0})
        </button>

        <button
          className="btn btn-dark btn-sm"
          onClick={() => {
            setIsModalOpen(true);
            setListMode("parent");
          }}
          disabled={parentList?.length === 0}
        >
          Parent User ({parentList?.length || 0})
        </button>
      </div>

      <Modal
        isOpen={isModalOpen}
        className="modal-xl"
        style={{
          backgroundColor: "#F7F7FA",
          borderRadius: "12px",
          padding: "0px",
        }}
        role="dialog"
        autoFocus={true}
        toggle={toggleRequestModel}
      >
        <div className="modal-header justify-content-between">
          <div className="d-flex align-items-center justify-content-center gap-4">
            <h5>
              {listMode === "shared" &&
                `Shared User List (${sharedList?.length || 0})`}
              {listMode === "parent" &&
                `ParentUser List (${parentList?.length || 0})`}
            </h5>
            {listMode === "shared" && (
              <Button
                onClick={() => setShareModal({ open: true, mode: "create" })}
                color="dark"
                className="d-flex align-items-center gap-2 py-1"
              >
                <i className="bx bx-group fs-4 "></i>
                Share Device Access
              </Button>
            )}
          </div>
          <button
            className="btn p-0 d-flex align-items-center"
            style={{ border: "none" }}
            onClick={cancleBtnClick}
            title="Close"
          >
            <box-icon name="x" size="30px"></box-icon>
          </button>
        </div>

        <ModalBody style={{ padding: "12px", backgroundColor: "#F7F7FA",minHeight:"300px" }}>
          {deleteError && (
            <Alert
              color="danger"
              className="d-flex justify-content-between align-items-center"
              toggle={() => setDeleteError(null)}
            >
              {deleteError}
            </Alert>
          )}
          <Row>
            {listType.length === 0 ? (
              <Col xs="12">
                <div className="text-center py-5 text-muted">
                  <i className="bx bx-user-x fs-1 d-block mb-2"></i>
                  <div className="fw-semibold">
                    {listMode === "shared"
                      ? "No shared users found"
                      : "No parent users found"}
                  </div>
                </div>
              </Col>
            ) : (
              listType.map((user: any, index: number) => (
                <Col sm="12" md="6" key={user?._id || user?.id || index}>
                  <CardUser
                    data={user}
                    showActions={listMode === "shared"}
                    onEdit={openEditModal}
                    onDelete={openDeleteModal}
                    isDeleting={
                      deletingId ===
                      (user?.accessId || user?._id || user?.id)
                    }
                  />
                </Col>
              ))
            )}
          </Row>
        </ModalBody>

        <ModalFooter
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <button
            className="btn btn-outline-secondary"
            onClick={cancleBtnClick}
            style={{ marginLeft: "auto" }}
          >
            Cancel
          </button>
        </ModalFooter>
      </Modal>

      <SharedUsersModal
        isOpen={shareModal.open}
        toggle={() => setShareModal((s) => ({ ...s, open: false }))}
        mode={shareModal.mode}
        editAccessData={shareModal.mode === "edit" ? shareModal.data : null}
        grantorUserId={grantorUserId}
        premiseId={premiseId}
        onSubmit={({ relation, selectedUser }) => {
          console.log(
            shareModal.mode === "edit" ? "Updated share for:" : "Shared with:",
            selectedUser,
            "as",
            relation,
          );
          onShareSuccess?.();
        }}
      />

      {/* confirmation modal for delete, replacing window.confirm */}
      {deleteModalOpen && (
        <ConfirmationModal
          headerTitle={listMode === "shared" ? "Remove Access?" : "Exit Parent Account "}
          text={listMode === "shared" ?  `Are you sure you want to remove "${
            [deleteTarget?.firstname, deleteTarget?.lastname]
              .filter(Boolean)
              .join(" ") || "this user"
          }'s" device access? This cannot be undone.` : `Are you sure you want to exit "${
            [deleteTarget?.firstname, deleteTarget?.lastname]
              .filter(Boolean)
              .join(" ") || "this user"
          }" Parent account? This cannot be undone.`}
          buttonText="Delete"
          onClick={confirmDelete}
          cancleBtnClick={() => {
            setDeleteModalOpen(false);
            setDeleteTarget(null);
          }}
          loading={
            deletingId ===
            (deleteTarget?.accessId || deleteTarget?._id || deleteTarget?.id)
          }
        />
      )}
    </div>
  );
}

export default SharedUsersList;

export const customStyles: any = {
  subHeader: {
    style: {
      padding: "0px",
      margin: "0px",
    },
  },
  headRow: {
    style: {
      backgroundColor: "#EFF2F7",
      fontWeight: "600",
      fontSize: "11px",
      borderBottom: "none",
    },
  },
  rows: {
    style: {
      fontSize: "11px",
      color: "#343a40",
      borderBottom: "none",
    },
  },
  cells: {
    style: {
      fontSize: "11px",
      color: "#343a40",
      borderBottom: "none",
    },
  },
};

// added onEdit / onDelete / showActions / isDeleting props
export const CardUser = ({
  data,
  onEdit,
  onDelete,
  showActions = true,
  isDeleting = false,
}: {
  data: any;
  onEdit?: (data: any) => void;
  onDelete?: (data?: any) => void;
  showActions?: boolean;
  isDeleting?: boolean;
}) => {
  const [showTable, setShowTable] = useState<
    "Chips" | "Lock" | "Blasters" | "Curtain" | "Driver" | null
  >(null);

  const Chipscolumns: any[] = [
    {
      name: "Slave",
      selector: (row: any) => row.slave || "-",
      sortable: true,
      wrap: true,
      width: "125px", // fixed width
    },
    {
      name: "Room Name",
      selector: (row: any) => row.roomName || "-",
      width: "100px",
      wrap: true,
    },

    {
      name: "Status",
      selector: (row: any) => (row.isOnline ? "Online" : "Offline"),
      cell: (row: any) =>
        row.isOnline === true ? (
          <span className="font-size-11 badge-soft-success badge bg-secondary">
            online
          </span>
        ) : (
          <span className="font-size-11 badge-soft-danger badge bg-secondary">
            Offline
          </span>
        ),
      width: "70px",
      wrap: true,
    },

    {
      name: "Model",
      selector: (row: any) => row.modelType || "-",
      width: "70px",
      wrap: true,
    },
    {
      name: "Product",
      selector: (row: any) => row.product || "-",
      width: "80px",
      wrap: true,
    },
    {
      name: "HW-V",
      selector: (row: any) => row.hwVersion || "-",
      // sortable: true,
      width: "70px",
      wrap: true,
    },
    {
      name: "WI-V",
      selector: (row: any) => row.wifiVersion || "-",
      // sortable: true,
      width: "70px",
      wrap: true,
    },
    {
      name: "Signal",
      selector: (row: any) => row.signalStrength ?? "-",
      // sortable: true,
      width: "70px",
      wrap: true,
    },
  ];

  const blasterColumns: any[] = [
    {
      name: "Slave",
      selector: (row: any) => row.slave || "-",
      wrap: true,
      width: "130px", // fixed width
    },
    {
      name: "HW-V",
      selector: (row: any) => row.hwVersion || "-",
      width: "80px",
      wrap: true,
    },

    {
      name: "WI-V",
      selector: (row: any) => row.wifiVersion || "-",
      width: "80px",
      wrap: true,
    },
    {
      name: "Name",
      selector: (row: any) => row.name || "-",
      width: "80px",
      wrap: true,
    },

    {
      name: "Status",
      selector: (row: any) => (row.isOnline ? "Online" : "Offline"),
      cell: (row: any) =>
        row.isOnline === true ? (
          <span className="font-size-11 badge-soft-success badge bg-secondary">
            online
          </span>
        ) : (
          <span className="font-size-11 badge-soft-danger badge bg-secondary">
            Offline
          </span>
        ),
      width: "70px",
      wrap: true,
    },
  ];

  const Lockcolumns: any[] = [
    {
      name: "Name",
      selector: (row: any) => row.name || "-",
      sortable: true,
      wrap: true,
      width: "130px", // fixed width
    },
    {
      name: "Room Name",
      selector: (row: any) => row.roomName || "-",
      width: "100px",
      wrap: true,
    },
  ];

  const CurtainColumns: any[] = [
    {
      name: "Slave",
      selector: (row: any) => row.slave || "-",
      wrap: true,
      width: "120px", // fixed width
    },
    {
      name: "Room",
      selector: (row: any) => row.roomName || "-",
      width: "80px",
      wrap: true,
    },
    {
      name: "HW-V",
      selector: (row: any) => row.hwVersion || "-",
      width: "70px",
      wrap: true,
    },
    {
      name: "WI-V",
      selector: (row: any) => row.wifiVersion || "-",
      width: "70px",
      wrap: true,
    },

    {
      name: "Model",
      selector: (row: any) => row.modelType || "-",
      width: "70px",
      wrap: true,
    },
    {
      name: "Signal",
      selector: (row: any) => row.signalStrength || "-",
      width: "70px",
      wrap: true,
    },
    {
      name: "Status",
      selector: (row: any) => (row.isOnline ? "Online" : "Offline"),
      cell: (row: any) =>
        row.isOnline === true ? (
          <span className="font-size-11 badge-soft-success badge bg-secondary">
            online
          </span>
        ) : (
          <span className="font-size-11 badge-soft-danger badge bg-secondary">
            Offline
          </span>
        ),
      width: "70px",
      wrap: true,
    },
  ];

  const DriverColumns: any[] = [
    {
      name: "Slave",
      selector: (row: any) => row.slave,
      wrap: true,
      width: "120px", // fixed width
    },
    {
      name: "Name",
      selector: (row: any) => row.name,
      wrap: true,
      width: "100px",
    },
    {
      name: "Node ID",
      selector: (row: any) => row?.nodeId ?? "-",
      cell: (row: any) =>
        row?.nodeId != null && row?.nodeId !== "" ? (
          <span
            style={{
              color: "#0d6efd",
              fontWeight: 600,
            }}
            title="View node in network info"
          >
            {row.nodeId}
          </span>
        ) : (
          <span>-</span>
        ),
      sortable: true,
      grow: 0.8,
      wrap: true,
      width: "100px",
    },
    {
      name: "macAddress",
      selector: (row: any) => row?.macAddress ?? "-",
      width: "180px",
      sortable: true,
      wrap: true,
    },
    {
      name: "uuidDigit",
      selector: (row: any) => row?.uuidDigit ?? "-",
      width: "100px",
    },
    {
      name: "UUID",
      selector: (row: any) => row?.uuid ?? "-",
      sortable: true,
      wrap: true,
      width: "180px",
    },
    {
      name: "Device Key",
      selector: (row: any) => row?.devKey ?? "-",
      wrap: true,
      sortable: true,
      width: "180px",
    },
    {
      name: "Type",
      selector: (row: any) => row?.deviceTypeTag || "-",
      sortable: true,
      grow: 0.5,
      wrap: true,
      width: "80px",
    },
    {
      name: "Configured",
      selector: (row: any) => (row?.isConfigured ? "Yes" : "No"),
      cell: (row: any) =>
        row?.isConfigured ? (
          <span className="font-size-11 badge-soft-success badge bg-success">
            Yes
          </span>
        ) : (
          <span className="font-size-11 badge-soft-danger badge bg-danger">
            No
          </span>
        ),
      sortable: true,
      grow: 0.8,
      wrap: true,
      width: "100px",
    },
    {
      name: "Status",
      selector: (row: any) => (row?.isOnline ? "Online" : "Offline"),
      cell: (row: any) =>
        row?.isOnline ? (
          <span className="font-size-11 badge-soft-success badge bg-success">
            Online
          </span>
        ) : (
          <span className="font-size-11 badge-soft-danger badge bg-danger">
            Offline
          </span>
        ),
      sortable: true,
      grow: 0.5,
      wrap: true,
      width: "100px",
    },
    {
      name: "Power",
      selector: (row: any) => (row?.isOn ? "ON" : "OFF"),
      cell: (row: any) =>
        row?.isOn ? (
          <span className="font-size-11 badge-soft-success badge bg-success">
            ON
          </span>
        ) : (
          <span className="font-size-11 badge-soft-danger badge bg-danger">
            OFF
          </span>
        ),
      sortable: true,
      grow: 0.5,
      wrap: true,
      width: "100px",
    },
  ];

  const { columns, tableData } = useMemo(() => {
    if (!showTable) {
      return { columns: [], tableData: [] };
    }

    if (showTable === "Chips") {
      return {
        columns: Chipscolumns,
        tableData: data?.chips ?? [],
      };
    }

    if (showTable === "Lock") {
      return {
        columns: Lockcolumns,
        tableData: data?.locks ?? [],
      };
    }

    if (showTable === "Blasters") {
      return {
        columns: blasterColumns,
        tableData: data?.blasters ?? [],
      };
    }

    if (showTable === "Curtain") {
      return {
        columns: CurtainColumns,
        tableData: data?.airCurtainDetails ?? [],
      };
    }

    if (showTable === "Driver") {
      return {
        columns: DriverColumns,
        tableData: data?.airDriverDetails ?? [],
      };
    }

    return { columns: [], tableData: [] };
  }, [showTable]);

  return (
    <Card className="border-0 shadow-sm rounded-3">
      <CardBody className="px-4 pt-4 pb-2">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="d-flex align-items-center">
            <a
              href={`/users/profile/${data?.sharedUserId}`}
              target="_blank"
              className="rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center flex-shrink-0 me-2"
              style={{
                width: 32,
                height: 32,
                fontWeight: 700,
                fontSize: 13,
              }}
            >
              {(data?.firstname?.[0] || "?").toUpperCase()}
              {(data?.lastname?.[0] || "").toUpperCase()}
            </a>

            <div>
              <div className="d-flex align-items-center gap-2">
                <CardTitle tag="h5" className="mb-0 fw-semibold">
                  <a
                    href={`/users/profile/${data?.sharedUserId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary text-decoration-none"
                  >
                    {data?.firstname} {data?.lastname}
                  </a>
                </CardTitle>

                {data?.isActive && (
                  <Badge
                    color="success"
                    pill
                    className="px-2 py-1"
                    style={{ fontSize: 12 }}
                  >
                    <i className="bx bx-check-circle me-1" />
                    Active
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* gated on showActions, wired to onEdit/onDelete, delete shows a spinner while in flight */}
          {showActions ? (
            <div className="d-flex gap-2">
              <Button
                color="link"
                className="p-0 text-primary shadow-none "
                title="Edit share access"
                onClick={() => onEdit?.(data)}
              >
                <i className="bx bx-pencil fs-5"></i>
              </Button>
              <Button
                color="link"
                className="p-0 text-danger shadow-none "
                title="Remove access"
                disabled={isDeleting}
                onClick={() => onDelete?.(data)}
              >
                {isDeleting ? (
                  <Spinner size="sm" />
                ) : (
                  <i className="bx bx-trash fs-5"></i>
                )}
              </Button>
            </div>
          ) : (
            <Button
              color="link"
              className="d-inline-flex align-items-center justify-content-center gap-1 py-1 rounded-2 border-0 text-dark bg-dark-subtle shadow-none fw-medium"
              title="Remove access"
              disabled={isDeleting}
              onClick={() => onDelete?.(data)}
              style={{
                fontSize: "13px",
                minWidth: "60px",
              }}
            >
              {isDeleting ? (
                <Spinner size="sm" />
              ) : (
                <>
                  <i className="bx bx-log-out fs-6" />
                  <span>Exit</span>
                </>
              )}
            </Button>
          )}
        </div>

        <Row className="mb-2">
          <Col md={6} className="border-end">
            <div className="d-flex flex-column gap-1">
              <div className="d-flex align-items-center">
                <i className="bx bx-phone text-primary fs-5 me-3" />
                <div>
                  <span className="text-muted me-3">Mobile:</span>
                  <strong>{data?.mobile || "-"}</strong>
                </div>
              </div>

              <div className="d-flex align-items-center">
                <i className="bx bx-envelope text-primary fs-5 me-3" />
                <div>
                  <span className="text-muted me-3">Email:</span>
                  <strong>{data?.email || "-"}</strong>
                </div>
              </div>

              <div className="d-flex align-items-center">
                <i className="bx bx-group text-primary fs-5 me-3" />
                <div>
                  <span className="text-muted me-3">Relation:</span>
                  <strong>{data?.relation || "-"}</strong>
                </div>
              </div>
            </div>
          </Col>

          <Col md={6} className="ps-md-4 mt-3 mt-md-0">
            <div className="fw-semibold mb-2">Rooms</div>

            <div className="mt-1 d-flex flex-wrap gap-2">
              {data?.roomNames?.map((room: any, idx: any) => (
                <Badge color="primary" className="px-1 py-1 fs-7" key={idx}>
                  {room}
                </Badge>
              ))}
            </div>
          </Col>
        </Row>

        <div className="border-top pt-1">
          <Row>
            <Col className="d-flex gap-2 p-2">
              <div>
                <button
                  type="button"
                  className={`btn btn-sm ${
                    showTable === "Chips" ? "btn-dark" : "btn-outline-dark"
                  } d-flex justify-content-between align-items-center`}
                  onClick={() =>
                    setShowTable((prev) => (prev === "Chips" ? null : "Chips"))
                  }
                  disabled={data?.chips?.length === 0}
                >
                  <span>
                    <strong>Chips ({data?.chips?.length || 0})</strong>
                  </span>
                </button>
              </div>
              <div>
                <button
                  type="button"
                  className={`btn btn-sm ${
                    showTable === "Blasters" ? "btn-dark" : "btn-outline-dark"
                  } d-flex justify-content-between align-items-center`}
                  onClick={() =>
                    setShowTable((prev) =>
                      prev === "Blasters" ? null : "Blasters",
                    )
                  }
                  disabled={data?.blasters?.length === 0}
                >
                  <span>
                    <strong>Blaster ({data?.blasters?.length || 0})</strong>
                  </span>
                </button>
              </div>
              <div>
                <button
                  type="button"
                  className={`btn btn-sm ${
                    showTable === "Lock" ? "btn-dark" : "btn-outline-dark"
                  } d-flex justify-content-between align-items-center`}
                  onClick={() =>
                    setShowTable((prev) => (prev === "Lock" ? null : "Lock"))
                  }
                  disabled={data?.locks?.length === 0}
                >
                  <span>
                    <strong>Lock ({data?.locks?.length || 0})</strong>
                  </span>
                </button>
              </div>

              <div>
                <button
                  type="button"
                  className={`btn btn-sm ${
                    showTable === "Curtain" ? "btn-dark" : "btn-outline-dark"
                  } d-flex justify-content-between align-items-center`}
                  onClick={() =>
                    setShowTable((prev) =>
                      prev === "Curtain" ? null : "Curtain",
                    )
                  }
                  disabled={data?.airCurtainDetails?.length === 0}
                >
                  <span>
                    <strong>
                      Curtain ({data?.airCurtainDetails?.length || 0})
                    </strong>
                  </span>
                </button>
              </div>
              <div>
                <button
                  type="button"
                  className={`btn btn-sm ${
                    showTable === "Curtain" ? "btn-dark" : "btn-outline-dark"
                  } d-flex justify-content-between align-items-center`}
                  onClick={() =>
                    setShowTable((prev) =>
                      prev === "Driver" ? null : "Driver",
                    )
                  }
                  disabled={data?.airDriverDetails?.length === 0}
                >
                  <span>
                    <strong>
                      Driver ({data?.airDriverDetails?.length || 0})
                    </strong>
                  </span>
                </button>
              </div>
            </Col>
          </Row>
        </div>

        {showTable && (
          <Row className="mt-3">
            <Col>
              <DataTable
                columns={columns}
                data={tableData}
                noDataComponent="No records found."
                customStyles={customStyles}
                highlightOnHover
                dense
              />
            </Col>
          </Row>
        )}
      </CardBody>
    </Card>
  );
};